import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PasswordResetService, PasswordResetTokenStatus } from '../services/password-reset.service';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService, NgxSpinnerComponent } from 'ngx-spinner';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

enum TokenStatus {
  LOADING = 'loading',
  VALID = 'valid',
  EXPIRED = 'expired',
  USED = 'used',
  INVALID = 'invalid'
}

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxSpinnerComponent, TranslocoModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.css'
})
export class ResetPasswordComponent implements OnInit {
  token: string = '';
  tokenStatus: TokenStatus = TokenStatus.LOADING;
  tokenStatusMessage: string = '';
  
  newPassword: string = '';
  confirmPassword: string = '';
  
  newPasswordError: string = '';
  confirmPasswordError: string = '';
  
  isSubmitted: boolean = false;
  isLoading: boolean = false;
  isSuccess: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private passwordResetService: PasswordResetService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    // Verificar se já está logado
    const jwtToken = localStorage.getItem('jwt');
    if (jwtToken) {
      this.router.navigate(['/home']);
      return;
    }

    // Extrair token da URL
    this.route.queryParams.subscribe(params => {
      this.token = params['token'] || '';
      
      if (!this.token) {
        this.tokenStatus = TokenStatus.INVALID;
        this.tokenStatusMessage = this.transloco.translate('resetPassword.toast.linkInvalid');
        return;
      }

      // Validar token
      this.validateToken();
    });
  }

  validateToken(): void {
    this.tokenStatus = TokenStatus.LOADING;
    this.spinner.show(undefined, {
      bdColor: 'rgba(0, 0, 0, 0.7)',
      size: 'medium',
      color: '#ffffff',
      type: 'ball-grid-pulse',
      fullScreen: true
    });

    this.passwordResetService.validateToken(this.token).subscribe({
      next: (response: PasswordResetTokenStatus) => {
        this.spinner.hide();
        
        if (response.isValid && !response.isExpired && !response.isUsed) {
          this.tokenStatus = TokenStatus.VALID;
          this.tokenStatusMessage = response.message || 'Token válido';
        } else if (response.isExpired || response.isUsed) {
          // Mensagem genérica para tokens expirados ou usados
          this.tokenStatus = response.isExpired ? TokenStatus.EXPIRED : TokenStatus.USED;
          this.tokenStatusMessage = this.transloco.translate('resetPassword.toast.linkExpired');
        } else {
          this.tokenStatus = TokenStatus.INVALID;
          this.tokenStatusMessage = this.transloco.translate('resetPassword.toast.linkInvalid');
        }
      },
      error: (error) => {
        this.spinner.hide();
        this.tokenStatus = TokenStatus.INVALID;
        this.tokenStatusMessage = this.transloco.translate('resetPassword.toast.validateError');
        
        if (error.status === 0 || error.status >= 500) {
          this.toastr.error(this.transloco.translate('resetPassword.toast.connectionError'));
        }
      }
    });
  }

  validatePasswords(): boolean {
    this.newPasswordError = '';
    this.confirmPasswordError = '';
    let isValid = true;

    // Validar nova senha
    if (!this.newPassword) {
      this.newPasswordError = this.transloco.translate('resetPassword.toast.passwordRequired');
      isValid = false;
    } else if (this.newPassword.length < 6) {
      this.newPasswordError = this.transloco.translate('resetPassword.toast.passwordMin');
      isValid = false;
    } else if (this.newPassword.length > 100) {
      this.newPasswordError = this.transloco.translate('resetPassword.toast.passwordMax');
      isValid = false;
    }

    // Validar confirmação de senha
    if (!this.confirmPassword) {
      this.confirmPasswordError = this.transloco.translate('resetPassword.toast.confirmRequired');
      isValid = false;
    } else if (this.newPassword && this.confirmPassword !== this.newPassword) {
      this.confirmPasswordError = this.transloco.translate('resetPassword.toast.passwordsMismatch');
      isValid = false;
    }

    return isValid;
  }

  onSubmit(): void {
    this.isSubmitted = true;
    
    if (!this.validatePasswords()) {
      return;
    }

    this.isLoading = true;
    this.spinner.show(undefined, {
      bdColor: 'rgba(0, 0, 0, 0.7)',
      size: 'medium',
      color: '#ffffff',
      type: 'ball-grid-pulse',
      fullScreen: true
    });

    this.passwordResetService.resetPassword(this.token, this.newPassword).subscribe({
      next: () => {
        // Definir sucesso quando a API retornar sucesso
        this.isSuccess = true;
        this.isSubmitted = true;
        this.spinner.hide();
        this.isLoading = false;
        
        // Não mostrar toastr para não sobrepor a tela de sucesso
        // A mensagem já está na tela de sucesso
        // Usuário volta manualmente através do link
      },
      error: (error) => {
        // Em caso de erro, garantir que sucesso não seja exibido
        this.isSuccess = false;
        this.isSubmitted = false;
        this.spinner.hide();
        this.isLoading = false;
        
        if (error.status === 400) {
          const errorMessage = error.error?.message || this.transloco.translate('resetPassword.toast.resetError');
          this.toastr.error(errorMessage);
          
          // Se o erro for relacionado ao token, atualizar status
          if (errorMessage.toLowerCase().includes('token') || 
              errorMessage.toLowerCase().includes('expirado') ||
              errorMessage.toLowerCase().includes('usado') ||
              errorMessage.toLowerCase().includes('inválido')) {
            this.validateToken();
          }
        } else if (error.status === 0 || error.status >= 500) {
          this.toastr.error(this.transloco.translate('resetPassword.toast.connectionError'));
        } else {
          this.toastr.error(this.transloco.translate('resetPassword.toast.resetErrorRetry'));
        }
      }
    });
  }

  goToForgotPassword(): void {
    this.router.navigate(['/forgot-password']);
  }

  goToLogin(): void {
    // Redirecionar para login na aba "Usuário e Senha" (password)
    this.router.navigate(['/login'], { queryParams: { tab: 'password' } });
  }

  get TokenStatus() {
    return TokenStatus;
  }
}
